import type { ComponentType } from 'react';
import { useState, useCallback, useEffect, useMemo, memo, useRef } from 'react';
import styled from '@emotion/styled';
import { Box, Typography, Skeleton } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import {
  type WidgetInstance,
  type WidgetChart,
  SegmentationDashboardWidget,
  type QuickChartData,
} from '@trinity/chat';
import { primaryColorOrSentimentFallback } from '@trinity/chat-components/components/Theme';
import { useCurrentSegmentIdsContext } from '@trinity/chat-components/containers/Segmentation.Dashboard/context';
import {
  useWidgetGridWidgetsContext,
  useWidgetMapContext,
} from '@trinity/chat-components/containers/WidgetGrid/context';
import { ActivityIndicator } from '../presentation';
import { ProgressBarScaleWrapper, ProgressBarScaleContent } from './ProgressBarPreviewScaling';
import { SemiCircleScaleWrapper, SemiCircleScaleContent } from './SemiCircleProgressPreviewScaling';
import { WidgetSourceInfo } from './WidgetSourceInfo';

export interface WidgetSelectChartStepProps {
  pickerOpen: boolean;
  onSelectedChange: (widget: WidgetInstance | null) => void;
  widgetType: 'chart' | 'kpi';
  /** Refreshes the host's widgets query on open; defaults to the legacy segment dashboard query key. */
  onOpenRefetch?: () => void;
}

export const WidgetSelectChartStep = ({
  pickerOpen,
  onSelectedChange,
  widgetType,
  onOpenRefetch,
}: WidgetSelectChartStepProps) => {
  const utils = useQueryClient();
  const [selected, setSelected] = useState<WidgetInstance | null>(null);
  const { widgets, widgetsLoading } = useWidgetGridWidgetsContext();
  const { projectId, segmentId } = useCurrentSegmentIdsContext();
  const widgetMap = useWidgetMapContext();

  // When the picker opens, force-refresh available widgets and clear selection
  useEffect(() => {
    if (!pickerOpen) return;
    setSelected(null);
    // Ensure a fresh fetch instead of only marking stale
    if (onOpenRefetch) {
      onOpenRefetch();
      return;
    }
    utils.refetchQueries({
      queryKey: ['available-widgets', projectId, segmentId],
    }).catch(() => undefined);
  }, [pickerOpen, projectId, segmentId, utils, onOpenRefetch]);

  // Notify parent when selection changes
  useEffect(() => {
    onSelectedChange(selected);
  }, [selected, onSelectedChange]);

  const handleSelect = useCallback((w: WidgetInstance) => {
    setSelected(w);
  }, []);

  const previewComponentFor = useCallback(
    (w: WidgetInstance) => {
      const Comp = widgetMap[w.type] as ComponentType<unknown> | undefined;
      if (!Comp || !w.props) return null;
      return <Comp key={w.id} {...(w.props as object)} />;
    },
    [widgetMap]
  );

  const filteredWidgets = useMemo(() => {
    const charts = widgets.filter(isChartWidget);
    if (widgetType === 'kpi') {
      return charts.filter((w) => getChartType(w) === 'kpi-widget');
    }
    return charts.filter((w) => getChartType(w) !== 'kpi-widget');
  }, [widgets, widgetType]);

  if (widgetsLoading) {
    return <ActivityIndicator />;
  }

  return (
    <Box
      sx={{
        height: 'auto',
        overflowY: 'auto',
        pr: 1,
        /* Optional smoother scrolling */
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { width: '8px' },
        '&::-webkit-scrollbar-track': { background: 'transparent' },
        '&::-webkit-scrollbar-thumb': {
          background: 'var(--gray-l)',
          borderRadius: '4px',
        },
      }}
      role="list"
    >
      <Box
        sx={{
          columnCount: { xs: 1, md: 2, lg: 3 },
          columnGap: 2,
        }}
      >
        {filteredWidgets.map((w) => (
          <WidgetPreviewCard
            key={w.id}
            widget={w}
            isSelected={selected?.id === w.id}
            onSelect={handleSelect}
            renderPreview={previewComponentFor}
          />
        ))}
      </Box>
    </Box>
  );
};

const ChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '18px',
  padding: '12px',
  gap: '6px',
  minHeight: '220px',
  cursor: 'pointer',
  transition: 'border-color 120ms ease, box-shadow 120ms ease',
  border: `2px solid ${theme.palette.gray.light2}`,
  // Reduce layout & paint work while scrolling
  contentVisibility: 'auto',
  containIntrinsicSize: '220px',
  willChange: 'transform',

  '&:hover, &.selected': {
    borderColor: primaryColorOrSentimentFallback(theme),
    backgroundColor: theme.palette.wl.enabled
      ? theme.palette.btnPrimary.bg
      : theme.palette.sentiment.primary.light,
  },
}));

// Lazy preview card: renders chart only when in viewport
const WidgetPreviewCard = memo(function WidgetPreviewCard({
  widget,
  isSelected,
  onSelect,
  renderPreview,
}: {
  widget: WidgetChart;
  isSelected: boolean;
  onSelect: (w: WidgetInstance) => void;
  renderPreview: (w: WidgetInstance) => React.ReactNode;
}) {
  const [ref, inView] = useInViewport({ rootMargin: '600px' });
  const [ready, setReady] = useState(false);
  const preview = renderPreview(widget);

  // Progressive mount once in view to avoid main-thread spikes
  useEffect(() => {
    if (!inView || ready) return;
    const id = setTimeout(() => setReady(true), 10);
    return () => clearTimeout(id);
  }, [inView, ready]);

  const chartType = getChartType(widget);
  const isProgressBarWidget = chartType === 'progress-bar-widget';
  const isSemiCircleProgressWidget = chartType === 'semi-circle-progress-widget';

  return (
    <Box sx={{ breakInside: 'avoid', mb: 2 }}>
      <ChartContainer onClick={() => onSelect(widget)} className={isSelected ? 'selected' : ''}>
        <Typography fontSize={13} fontFamily="var(--font-semibold)" noWrap>
          {widget.title}
        </Typography>
        <Box
          ref={ref}
          flex={1}
          overflow="hidden"
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderRadius={1}
          minHeight={180}
        >
          {(() => {
            if (!inView || !ready) return <Skeleton variant="rounded" width="100%" height={180} />;
            if (isProgressBarWidget) {
              return (
                <ProgressBarScaleWrapper>
                  <ProgressBarScaleContent>{preview}</ProgressBarScaleContent>
                </ProgressBarScaleWrapper>
              );
            }
            if (isSemiCircleProgressWidget) {
              return (
                <SemiCircleScaleWrapper>
                  <SemiCircleScaleContent>{preview}</SemiCircleScaleContent>
                </SemiCircleScaleWrapper>
              );
            }
            return (
              preview || (
                <Typography fontSize={12} color="var(--gray-d)">
                  No preview
                </Typography>
              )
            );
          })()}
        </Box>
        {widget.sourceMetadata && (
          <WidgetSourceInfo sourceMetadata={widget.sourceMetadata} projectSlug={null} />
        )}
      </ChartContainer>
    </Box>
  );
});

function isChartWidget(widget: WidgetInstance): widget is WidgetChart {
  return widget.type === SegmentationDashboardWidget.ChartVisualization;
}

function getChartType(widget: WidgetChart): QuickChartData['type'] | undefined {
  return widget.props?.chart.type;
}

// Simple IntersectionObserver hook without external deps
function useInViewport({ rootMargin = '0px' }: { rootMargin?: string }) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
            break;
          }
        }
      },
      { root: null, rootMargin, threshold: 0.01 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [rootMargin]);

  return [ref, inView] as const;
}
